import { defineMessages } from "../define";

/** Monitoring board, tab texts: GPU pods (monitoring.pods.*) and backend (monitoring.backend.*). */
export default defineMessages({
  en: {
    // --- GPU pods: pool ------------------------------------------------------------------------------
    "monitoring.pods.updated": "updated",
    "monitoring.pods.on": "on",
    "monitoring.pods.off": "off",
    "monitoring.pods.never": "never",
    "monitoring.pods.pool.title": "Pod pool",
    "monitoring.pods.pool.description": "Live state of this backend's GPU pod pool, refreshed every 5 s",
    "monitoring.pods.pool.none":
      "The pod pool does not exist yet: the backend creates it with the first evaluation after it starts.",
    "monitoring.pods.tile.pods": "Healthy pods",
    "monitoring.pods.tile.podsValue": "{healthy} / {total}",
    "monitoring.pods.tile.noPods": "None",
    "monitoring.pods.tile.noPodsSub": "every evaluation fails",
    "monitoring.pods.tile.allHealthy": "all healthy",
    "monitoring.pods.tile.unhealthy.one": "{count} unhealthy",
    "monitoring.pods.tile.unhealthy.few": "{count} unhealthy",
    "monitoring.pods.tile.unhealthy.other": "{count} unhealthy",
    "monitoring.pods.tile.inFlight": "In flight",
    "monitoring.pods.tile.inFlightSub": "of {max} concurrent slots",
    "monitoring.pods.tile.queued": "Queued",
    "monitoring.pods.tile.queuedSub": "waiting for a free slot",
    "monitoring.pods.tile.timeout": "Request timeout",
    "monitoring.pods.tile.timeoutSub": "up to {attempts} attempts, each on another pod",
    "monitoring.pods.tile.attempts": "Attempt deadlines",
    "monitoring.pods.tile.attemptsCount.one": "{count} attempt",
    "monitoring.pods.tile.attemptsCount.few": "{count} attempts",
    "monitoring.pods.tile.attemptsCount.other": "{count} attempts",
    "monitoring.pods.tile.attemptsSub": "at most {total} s per call",
    "monitoring.pods.tile.attemptsHint":
      "A request that does not answer in time is sent again (another pod first, the same one when it is the only pod), each time with a longer deadline; after the last one the call fails.",
    "monitoring.pods.tile.keeper": "Connection keeper",
    "monitoring.pods.tile.keeperSub": "keeps the TCP window of idle pods warm",
    "monitoring.pods.tile.keeperHint":
      "While a pod is idle, the backend sends a small dummy upload to /drain every 0.2 s so that the warm connection's TCP congestion window never collapses.",
    "monitoring.pods.tile.keeper404.one": "{count} pod without /drain",
    "monitoring.pods.tile.keeper404.few": "{count} pods without /drain",
    "monitoring.pods.tile.keeper404.other": "{count} pods without /drain",
    "monitoring.pods.tile.session": "HTTP session",
    "monitoring.pods.tile.sessionOpen": "open",
    "monitoring.pods.tile.sessionClosed": "not open yet",
    "monitoring.pods.tile.sessionSub": "opened by the first inference request",
    "monitoring.pods.tile.source": "Pod list",
    "monitoring.pods.source.autoscaler": "Autoscaler",
    "monitoring.pods.source.autoscalerSub": "pod_servers snapshot",
    "monitoring.pods.source.static": "Static",
    "monitoring.pods.source.staticSub": "pod_pool_static_servers setting",
    "monitoring.pods.source.custom": "Custom",
    "monitoring.pods.source.customSub": "set in code",
    "monitoring.pods.tile.lastUpdate": "Autoscaler update",
    "monitoring.pods.tile.lastUpdateNever": "none yet",
    "monitoring.pods.tile.lastUpdateSub": "+{added} / −{removed} · {total} in the pool",
    "monitoring.pods.tile.lastUpdateStatic": "a static pool is never updated",
    "monitoring.pods.tile.lastUpdateWaiting": "no snapshot since the backend started",
    "monitoring.pods.update.title": "Last change of the pod set",
    "monitoring.pods.update.added": "Added",
    "monitoring.pods.update.removed": "Removed",
    "monitoring.pods.config.title": "Configured vs running",
    "monitoring.pods.config.summary": "{configured} in pod_servers · {running} in the pool",
    "monitoring.pods.config.mismatch":
      "The pool differs from the autoscaler snapshot in pod_servers (an update did not arrive or failed)",
    "monitoring.pods.config.missing": "In pod_servers, not running:",
    "monitoring.pods.config.extra": "Running, not in pod_servers:",
    "monitoring.pods.config.match": "The pool matches pod_servers.",
    "monitoring.pods.config.noPool": "Compared once the pool exists.",
    "monitoring.pods.config.static": "Static pool: pod_servers ({configured} entries) is not used.",
    "monitoring.pods.config.notUsed": "The pool does not come from pod_servers ({configured} entries); nothing to compare.",
    "monitoring.pods.config.unavailable": "pod_servers could not be read.",

    // --- GPU pods: cards ----------------------------------------------------------------------------
    "monitoring.pods.cards.title": "Pods",
    "monitoring.pods.cards.empty":
      "No pod in the pool. With the autoscaler as the source, every evaluation fails until it posts a snapshot with a pod.",
    "monitoring.pods.cards.noPool": "No pod yet: the pool is created by the first evaluation.",
    "monitoring.pods.health.healthy": "Healthy",
    "monitoring.pods.health.unhealthy": "Unhealthy",
    "monitoring.pods.health.failing": "Failing",
    "monitoring.pods.health.changed": "state changed",
    "monitoring.pods.health.unchanged": "no state change since start",
    "monitoring.pods.locality.none": "no locality",
    "monitoring.pods.ping.notReady": "not ready",
    "monitoring.pods.ping.never": "not pinged yet",
    "monitoring.pods.ping.notReadyDetail": "/ping does not answer 200: the pod is warming up or down",
    "monitoring.pods.stat.ping": "Ping",
    "monitoring.pods.stat.pingHint":
      "Fastest of 3 parallel /ping calls every 2 s; a busy healthy pod is not pinged, its traffic proves it is alive.",
    "monitoring.pods.stat.ewma": "Latency (EWMA)",
    "monitoring.pods.stat.ewmaSub": "successful requests",
    "monitoring.pods.stat.ewmaHint":
      "Exponentially smoothed latency of successful requests (α 0.3); kept for statistics, not used for routing.",
    "monitoring.pods.stat.inFlight": "In flight",
    "monitoring.pods.stat.inFlightSub": "requests on the pod now",
    "monitoring.pods.stat.requests": "Requests",
    "monitoring.pods.stat.requestsSub": "{ok} ok · {fail} failed · {timeout} timed out",
    "monitoring.pods.stat.failureRate": "Failure rate",
    "monitoring.pods.stat.sinceStart": "since the backend started",
    "monitoring.pods.stat.consecutive": "Failures in a row",
    "monitoring.pods.stat.consecutiveSub": "taken out at {limit}",
    "monitoring.pods.stat.lastSuccess": "Last success",
    "monitoring.pods.stat.lastFailure": "Last failure",
    "monitoring.pods.stat.deactivations": "Deactivations",
    "monitoring.pods.stat.deactivationsHint":
      "How often the pod was taken out of rotation since the backend started (3 failures in a row or a failed ping)",
    "monitoring.pods.stat.idle": "Idle",
    "monitoring.pods.stat.idleNever": "never used",
    "monitoring.pods.stat.idleHint": "Time since the pod last finished a real request (keeper bursts do not count)",
    "monitoring.pods.keeper.label": "Keeper",
    "monitoring.pods.keeper.running": "running",
    "monitoring.pods.keeper.stopped": "not running",
    "monitoring.pods.keeper.disabled": "disabled",
    "monitoring.pods.keeper.missingDrain": "/drain missing",
    "monitoring.pods.keeper.404Detail":
      "The pod has no /drain endpoint (old serving_backend build): every keeper burst closes a pooled connection. Redeploy the pod or disable the keeper.",
    "monitoring.pods.lastError": "Last error",
    "monitoring.pods.phases.title": "Last request",
    "monitoring.pods.phases.none": "No successful request since the backend started.",
    "monitoring.pods.phases.meta": "total {total} · {in} KB in · {out} KB out",
    "monitoring.pods.phase.lock": "lock",
    "monitoring.pods.phase.conn": "conn",
    "monitoring.pods.phase.upload": "upload",
    "monitoring.pods.phase.pod": "pod",
    "monitoring.pods.phase.read": "read",
    "monitoring.pods.phase.reused": "reused",
    "monitoring.pods.phase.new": "NEW",
    "monitoring.pods.phase.lockHint": "Waited for a keeper burst to release the warm connection",
    "monitoring.pods.phase.connHint":
      "Connection checkout: reused = warm pooled socket, NEW = handshake (the warm socket was lost)",
    "monitoring.pods.phase.uploadHint": "Handing the body to the kernel; grows only when the send buffer fills",
    "monitoring.pods.phase.podHint":
      "Body sent → response headers: the actual wire upload (a TCP slow start shows here), decoding, GPU inference and one round trip",
    "monitoring.pods.phase.readHint": "Response body download",
    "monitoring.pods.phase.newWarning":
      "New connection: the warm socket was lost, this request paid a handshake and a TCP slow start",
    "monitoring.pods.recent.title": "Latest requests",
    "monitoring.pods.recent.summary": "{n} since {time} · p50 {p50} · p95 {p95} · max {max}",
    "monitoring.pods.recent.failures.one": "{count} failed",
    "monitoring.pods.recent.failures.few": "{count} failed",
    "monitoring.pods.recent.failures.other": "{count} failed",
    "monitoring.pods.recent.empty": "No request on this pod since the backend started.",
    "monitoring.pods.recent.latency": "Latency",
    "monitoring.pods.recent.failed": "Failed",

    // --- GPU pods: the pod's own GET /metrics ---------------------------------------------------------
    "monitoring.pods.side.title": "On the pod",
    "monitoring.pods.side.polled": "polled",
    "monitoring.pods.side.pollHint": "The backend reads the pod's GET /metrics every 15 s",
    "monitoring.pods.side.unsupported": "Pod image without /metrics (older than v0.0.39): no GPU or pod-side data.",
    "monitoring.pods.side.waiting": "Waiting for the first /metrics poll (every 15 s).",
    "monitoring.pods.side.pollFailed": "GET /metrics failed: {error}",
    "monitoring.pods.side.lastGood": "last good poll",
    "monitoring.pods.side.image": "Image",
    "monitoring.pods.side.pcieOf": "(of {max})",
    "monitoring.pods.side.pcieHint":
      "The best PCIe link since the pod started (it trains down while the GPU idles); a narrower link than the card supports (x8 instead of x16) made pods slow before",
    "monitoring.pods.side.uptime": "up {duration}",
    "monitoring.pods.side.readyAfter": "ready after {duration}",
    "monitoring.pods.side.preloadFailed": "The models failed to load, the pod never becomes ready",
    "monitoring.pods.side.onCpuWarning": "Running on the CPU instead of the GPU (10–50× slower): {models}",
    "monitoring.pods.side.loopBlocked": "The pod's event loop was blocked up to {max} in 5 min",
    "monitoring.pods.side.noGpu": "No GPU data from the pod: {error}",
    "monitoring.pods.side.util": "GPU utilisation",
    "monitoring.pods.side.utilSub": "5 min avg {avg} · max {max}",
    "monitoring.pods.side.memory": "GPU memory",
    "monitoring.pods.side.memorySub": "of {total} · 5 min max {max}",
    "monitoring.pods.side.temperature": "Temperature",
    "monitoring.pods.side.temperatureSub": "5 min max {max}",
    "monitoring.pods.side.power": "Power",
    "monitoring.pods.side.powerSub": "limit {limit} · 5 min avg {avg}",
    "monitoring.pods.side.sparkHint": "The line: the last 15 min (a sample every 5 s), up to the card's limit",
    "monitoring.pods.side.throttle": "Clocks throttled",
    "monitoring.pods.side.throttleCapHint": "The card runs at its power limit: normal under load",
    "monitoring.pods.side.throttleSlowHint": "Clocks lowered by heat or the hardware: inference gets slower",
    "monitoring.pods.side.smClock": "SM",
    "monitoring.pods.side.models": "Models",
    "monitoring.pods.side.model.crust": "Crust",
    "monitoring.pods.side.model.components": "Components",
    "monitoring.pods.side.model.sauces": "Sauces",
    "monitoring.pods.side.model.obstacles": "Obstacles",
    "monitoring.pods.side.model.slicing": "Slicing",
    "monitoring.pods.side.onGpu": "GPU",
    "monitoring.pods.side.onCpu": "CPU",
    "monitoring.pods.side.notLoaded": "not loaded",
    "monitoring.pods.side.cpuHint": "Runs on the CPU (ONNX Runtime fell back): 10–50× slower",
    "monitoring.pods.side.nvjpegHint": "Photos are decoded on the GPU (nvJPEG)",
    "monitoring.pods.side.cpuDecode": "CPU decode",
    "monitoring.pods.side.cpuDecodeHint": "Photos are decoded on the CPU (nvJPEG is not active)",
    "monitoring.pods.side.routes": "Latency on the pod · 5 min",
    "monitoring.pods.side.routesHint":
      "Time inside the pod from receiving the request to the response, without the network",
    "monitoring.pods.side.col.count": "Requests",
    "monitoring.pods.side.col.errors": "Errors",
    "monitoring.pods.side.clientErrorsHint": "Refused requests (4xx), e.g. a crop outside the photo",
    "monitoring.pods.side.noInference": "No inference request since the pod started.",
    "monitoring.pods.side.otherRoutes": "Other requests in 5 min:",
    "monitoring.pods.side.other": "unknown paths",
    "monitoring.pods.side.otherHint": "Requests to unknown paths: internet scanners try the public pod port",
    "monitoring.pods.side.errors": "Last errors on the pod",
    "monitoring.pods.side.errorsAll": "Show all ({count})",
    "monitoring.pods.side.errorsFewer": "Show fewer",
    "monitoring.pods.side.noMessage": "no message",
    "monitoring.pods.side.details": "Pod details",
    "monitoring.pods.side.hideDetails": "Hide pod details",
    "monitoring.pods.side.stages": "Where the pod's time goes · p50 5 min",
    "monitoring.pods.side.stagesTotal": "whole request p50 {p50}",
    "monitoring.pods.side.stagesNote":
      "Bars: the stage's p50 as a share of the request's p50. /analyze/raw runs components, obstacles and slicing in parallel, so its stages add up to more.",
    "monitoring.pods.side.stagesEmpty": "No inference in the last 5 min.",
    "monitoring.pods.side.startup": "Startup (last run)",
    "monitoring.pods.side.process": "Event loop, process and host",
    "monitoring.pods.side.loop": "Event loop lag (5 min)",
    "monitoring.pods.side.loopValue": "max {max} · p99 {p99}",
    "monitoring.pods.side.loopHint":
      "How late a 0.5 s timer on the pod wakes up; the crust inference alone blocks the loop ~150–250 ms, under ~300 ms is normal",
    "monitoring.pods.side.over100": "Probes over 100 ms (5 min)",
    "monitoring.pods.side.over100Hint": "Loop probes (every 0.5 s) that woke up more than 100 ms late",
    "monitoring.pods.side.processCpu": "Process CPU",
    "monitoring.pods.side.rss": "Process memory (RSS)",
    "monitoring.pods.side.nowMax": "{now} · 5 min max {max}",
    "monitoring.pods.side.hostCpu": "Host CPU",
    "monitoring.pods.side.hostCpuValue": "{pct} · {cores} cores · load {load}",
    "monitoring.pods.side.hostMemory": "Host memory",
    "monitoring.pods.side.driver": "Driver",
    "monitoring.pods.side.pcieNow": "PCIe link now",
    "monitoring.pods.side.pcieNowHint": "The link trains down while the GPU idles (Gen1 at idle is normal)",
    "monitoring.pods.side.uptimeLabel": "Uptime",
    "monitoring.pods.side.podId": "RunPod pod ID",

    // --- GPU pods: window charts --------------------------------------------------------------------
    "monitoring.pods.window.title": "History · {window}",
    "monitoring.pods.window.gpu": "GPU on the pods · {window}",
    "monitoring.pods.chart.empty": "No inference in this window",
    "monitoring.pods.chart.requests": "Inference attempts",
    "monitoring.pods.chart.requestsDescription":
      "Attempts per bucket by outcome; a failed attempt is retried on another pod",
    "monitoring.pods.series.ok": "OK",
    "monitoring.pods.series.fail": "Failed",
    "monitoring.pods.series.timeout": "Timed out",
    "monitoring.pods.chart.latency": "Inference latency",
    "monitoring.pods.chart.latencyDescription":
      "Whole request incl. queue wait and retries; crust and components p95 in the legend",
    "monitoring.pods.series.crustP95": "Crust p95",
    "monitoring.pods.series.componentsP95": "Components p95",
    "monitoring.pods.chart.slow": "slow",
    "monitoring.pods.chart.perPod": "p95 latency per pod",
    "monitoring.pods.chart.perPodDescription": "One successful attempt on the pod.",
    "monitoring.pods.chart.extraPods": "Pods after the fourth are grey and hidden; show them in the legend.",
    "monitoring.pods.chart.ping": "Ping p95 per pod",
    "monitoring.pods.chart.pingDescription": "Round trip of /ping, measured while the pod is idle.",
    "monitoring.pods.chart.noPing": "No ping in this window",
    "monitoring.pods.chart.queue": "Queue wait",
    "monitoring.pods.chart.queueDescription": "How long a request waited for one of the {max} concurrent slots",
    "monitoring.pods.chart.noQueue": "No request had to wait in this window",
    "monitoring.pods.series.max": "max",
    "monitoring.pods.chart.concurrency": "In flight and queued",
    "monitoring.pods.chart.concurrencyDescription": "Peaks per bucket; a queue forms only when every slot is taken",
    "monitoring.pods.series.inFlight": "In flight (max)",
    "monitoring.pods.series.queued": "Queued (max)",
    "monitoring.pods.chart.slots": "limit {max}",
    "monitoring.pods.chart.noConcurrency": "Nothing in flight or queued at any sample (sampled every 10 s)",
    "monitoring.pods.chart.health": "Healthy pods",
    "monitoring.pods.chart.healthDescription":
      "Fewest healthy pods and the largest pool per bucket; red bars mark pods out of rotation",
    "monitoring.pods.series.healthy": "Healthy (min)",
    "monitoring.pods.series.total": "In the pool (max)",
    "monitoring.pods.series.unhealthy": "Unhealthy",
    "monitoring.pods.chart.noHealth": "No pool sample in this window",
    "monitoring.pods.chart.retries": "Retries and pool errors",
    "monitoring.pods.chart.retriesDescription":
      "Requests that succeeded only on a retry, and requests that failed on every pod (the evaluation fails)",
    "monitoring.pods.series.retriedOk": "OK after a retry",
    "monitoring.pods.series.poolError": "Failed on every pod",
    "monitoring.pods.chart.noRetries": "No retry and no pool error ({window})",
    "monitoring.pods.chart.gpuUtil": "GPU utilisation per pod",
    "monitoring.pods.chart.gpuUtilDescription": "Highest 1-min average per bucket, from the pods' own /metrics.",
    "monitoring.pods.chart.gpuMemory": "GPU memory per pod",
    "monitoring.pods.chart.gpuMemoryDescription": "Peak used GPU memory per bucket.",
    "monitoring.pods.chart.gpuTemperature": "GPU temperature per pod",
    "monitoring.pods.chart.gpuTemperatureDescription": "Peak per bucket; from {hot} °C the GPU counts as hot.",
    "monitoring.pods.chart.hot": "hot",
    "monitoring.pods.chart.gpuPower": "GPU power per pod",
    "monitoring.pods.chart.gpuPowerDescription": "Highest 1-min average per bucket.",
    "monitoring.pods.chart.podLoop": "Event loop lag on the pods",
    "monitoring.pods.chart.podLoopDescription":
      "Peak per bucket; the crust inference alone blocks a pod's loop ~150–250 ms.",
    "monitoring.pods.chart.noGpu":
      "No pod reported GPU metrics in this window (GET /metrics comes with pod image v0.0.39).",

    // --- GPU pods: totals ---------------------------------------------------------------------------
    "monitoring.pods.totals.title": "Per pod over the window",
    "monitoring.pods.totals.description": "Attempts, failures and latency of single attempts ({window})",
    "monitoring.pods.col.pod": "Pod",
    "monitoring.pods.col.attempts": "Attempts",
    "monitoring.pods.col.failures": "Failed",
    "monitoring.pods.col.failureRate": "Failure rate",
    "monitoring.pods.col.p50": "p50",
    "monitoring.pods.col.p95": "p95",
    "monitoring.pods.col.max": "max",
    "monitoring.pods.col.ping": "Ping p95",
    "monitoring.pods.totals.timeouts": "({count} timed out)",
    "monitoring.pods.totals.all": "All pods",
    "monitoring.pods.totals.note":
      "Per pod: latency of one successful attempt. All pods: the whole request incl. queue wait and retries.",
    "monitoring.pods.totals.notInPool": "no longer in the pool",
    "monitoring.pods.totals.empty": "No inference attempt in this window.",

    // --- backend: process and event loop -------------------------------------------------------------
    "monitoring.backend.on": "on",
    "monitoring.backend.off": "off",
    "monitoring.backend.process.title": "Process",
    "monitoring.backend.process.description": "The backend process this admin talks to",
    "monitoring.backend.process.instance": "Instance",
    "monitoring.backend.process.role": "Environment",
    "monitoring.backend.process.version": "Version",
    "monitoring.backend.process.pid": "PID",
    "monitoring.backend.process.hostname": "Hostname",
    "monitoring.backend.process.python": "Python",
    "monitoring.backend.process.platform": "Platform",
    "monitoring.backend.process.started": "Started",
    "monitoring.backend.process.uptime": "Uptime",
    "monitoring.backend.process.recentRestart":
      "Restarted less than 10 min ago: live data (pod counters, the last hour) start from zero.",
    "monitoring.backend.loop.title": "Event loop lag",
    "monitoring.backend.loop.description":
      "How late a 250 ms timer wakes up: a blocked loop (sync CPU work, a blocking call) shows here first",
    "monitoring.backend.loop.current": "Now",
    "monitoring.backend.loop.max1m": "Max 1 min",
    "monitoring.backend.loop.max5m": "Max 5 min",
    "monitoring.backend.loop.thresholds": "Warning from 300 ms, blocked from 1 s.",
    "monitoring.backend.loop.live": "Last hour · max per 5 s",
    "monitoring.backend.loop.window": "Max per bucket · {window}",
    "monitoring.backend.loop.lag": "Lag",
    "monitoring.backend.loop.warnLine": "warning",
    "monitoring.backend.loop.nokLine": "blocked",
    "monitoring.backend.loop.empty": "No lag sample yet",

    // --- backend: resources ---------------------------------------------------------------------------
    "monitoring.backend.res.title": "Resources",
    "monitoring.backend.res.description":
      "Current sample (every 10 s), the last hour of this instance and the peaks over the window",
    "monitoring.backend.res.sampled": "sampled",
    "monitoring.backend.res.cpu": "CPU",
    "monitoring.backend.res.cpuSub": "100 % = one core",
    "monitoring.backend.res.rss": "Memory (RSS)",
    "monitoring.backend.res.tasks": "Asyncio tasks",
    "monitoring.backend.res.threads": "Threads",
    "monitoring.backend.res.fds": "Open files",
    "monitoring.backend.res.writes": "Background S3 writes",
    "monitoring.backend.res.writesSub": "photo layers waiting for S3",
    "monitoring.backend.res.inference": "Inference in flight",
    "monitoring.backend.res.http": "HTTP in flight",
    "monitoring.backend.res.monitored": "Monitored tasks",
    "monitoring.backend.res.monitoredSub": "background tasks with error reporting",
    "monitoring.backend.res.live": "Last hour · samples every 10 s",
    "monitoring.backend.res.window": "Peaks per bucket · {window}",
    "monitoring.backend.res.chart.cpu": "CPU",
    "monitoring.backend.res.chart.memory": "Memory (RSS)",
    "monitoring.backend.res.chart.counts": "Tasks, threads and open files",
    "monitoring.backend.res.chart.work": "Work in progress",
    "monitoring.backend.res.chart.cpuMax": "CPU (max)",
    "monitoring.backend.res.chart.memoryMax": "Memory (max)",
    "monitoring.backend.res.chart.tasksMax": "Asyncio tasks (max)",
    "monitoring.backend.res.chart.workMax": "Work in progress (max)",
    "monitoring.backend.res.backlog": "backlog",
    "monitoring.backend.res.noSamples": "No sample yet (the sampler runs every 10 s)",
    "monitoring.backend.res.noWork": "Nothing in progress at any sample",

    // --- backend: dependencies, models, settings ------------------------------------------------------
    "monitoring.backend.deps.title": "Dependencies",
    "monitoring.backend.deps.description": "Reachability as checked by the backend (cached for 30 s)",
    "monitoring.backend.deps.checked": "checked",
    "monitoring.backend.deps.mongo": "MongoDB",
    "monitoring.backend.deps.s3": "S3 storage",
    "monitoring.backend.deps.thingsboard": "ThingsBoard",
    "monitoring.backend.deps.ok": "Reachable",
    "monitoring.backend.deps.error": "Unreachable",
    "monitoring.backend.deps.slow": "slow",
    "monitoring.backend.deps.pods": "GPU pods",
    "monitoring.backend.deps.podsEmpty": "No pod in the pool.",
    "monitoring.backend.deps.pingHint": "Last ping of the pod",
    "monitoring.backend.deps.empty": "The backend returned no check.",
    "monitoring.backend.models.title": "Models",
    "monitoring.backend.models.description": "ONNX models of this process; each one loads on first use",
    "monitoring.backend.models.loaded": "Loaded",
    "monitoring.backend.models.notLoaded": "Not loaded",
    "monitoring.backend.models.version": "Version",
    "monitoring.backend.models.threads": "Threads",
    "monitoring.backend.models.threshold": "Threshold",
    "monitoring.backend.models.empty": "No model information.",
    "monitoring.backend.models.name.burnt_cnn": "Burnt crust",
    "monitoring.backend.models.name.blur_cnn": "Blur check",
    "monitoring.backend.models.name.obstacle_seg": "Obstacles",
    "monitoring.backend.models.name.slicing_cnn": "Slicing",
    "monitoring.backend.settings.title": "Settings",
    "monitoring.backend.settings.description": "Snapshot of the settings that change behaviour (no secrets)",
    "monitoring.backend.settings.general": "General",
    "monitoring.backend.settings.empty": "The backend returned no settings.",

    // --- backend: monitoring health, integrations, instances ------------------------------------------
    "monitoring.backend.mon.title": "Monitoring health",
    "monitoring.backend.mon.description": "Whether the board's own data is being saved",
    "monitoring.backend.mon.persist": "Persistence",
    "monitoring.backend.mon.persistOn": "MongoDB",
    "monitoring.backend.mon.persistOff": "memory only",
    "monitoring.backend.mon.persistFailed": "memory only (setup failed)",
    "monitoring.backend.mon.metricsFlush": "Metrics saved",
    "monitoring.backend.mon.eventsFlush": "Events saved",
    "monitoring.backend.mon.never": "never",
    "monitoring.backend.mon.metricsError": "Saving metrics fails",
    "monitoring.backend.mon.eventsError": "Saving events fails",
    "monitoring.backend.mon.dropped": "Events dropped",
    "monitoring.backend.mon.droppedHint": "Events lost because the in-memory buffer overflowed",
    "monitoring.backend.mon.monitoredTasks": "Monitored tasks running",
    "monitoring.backend.mon.recorded": "Events since start",
    "monitoring.backend.int.title": "Integrations",
    "monitoring.backend.int.description": "Last calls received by this instance since it started",
    "monitoring.backend.int.biteberry.order": "BiteBerry orders",
    "monitoring.backend.int.biteberry.item": "BiteBerry items",
    "monitoring.backend.int.biteberry.status": "BiteBerry status",
    "monitoring.backend.int.autoscaler": "Autoscaler snapshots",
    "monitoring.backend.int.calls.one": "{count} call",
    "monitoring.backend.int.calls.few": "{count} calls",
    "monitoring.backend.int.calls.other": "{count} calls",
    "monitoring.backend.int.never": "none since start",
    "monitoring.backend.inst.title": "Instances",
    "monitoring.backend.inst.description":
      "Backend processes that wrote metrics in the last 24 h; scope the board to one of them",
    "monitoring.backend.inst.col.instance": "Instance",
    "monitoring.backend.inst.col.role": "Environment",
    "monitoring.backend.inst.col.first": "First seen",
    "monitoring.backend.inst.col.last": "Last seen",
    "monitoring.backend.inst.col.requests": "Requests (24 h)",
    "monitoring.backend.inst.col.action": "Scope",
    "monitoring.backend.inst.scope": "Show only this",
    "monitoring.backend.inst.scoped": "Shown",
    "monitoring.backend.inst.clear": "Back to the environment",
    "monitoring.backend.inst.empty": "No instance wrote metrics in the last 24 h (persistence off?).",
  },
  cs: {
    "monitoring.pods.updated": "aktualizováno",
    "monitoring.pods.on": "zapnuto",
    "monitoring.pods.off": "vypnuto",
    "monitoring.pods.never": "nikdy",
    "monitoring.pods.pool.title": "Pool podů",
    "monitoring.pods.pool.description": "Živý stav poolu GPU podů tohoto backendu, obnovuje se každých 5 s",
    "monitoring.pods.pool.none":
      "Pool podů zatím neexistuje: backend ho vytvoří při prvním vyhodnocení po startu.",
    "monitoring.pods.tile.pods": "Pody v pořádku",
    "monitoring.pods.tile.podsValue": "{healthy} / {total}",
    "monitoring.pods.tile.noPods": "Žádný",
    "monitoring.pods.tile.noPodsSub": "každé vyhodnocení selže",
    "monitoring.pods.tile.allHealthy": "všechny v pořádku",
    "monitoring.pods.tile.unhealthy.one": "{count} mimo provoz",
    "monitoring.pods.tile.unhealthy.few": "{count} mimo provoz",
    "monitoring.pods.tile.unhealthy.other": "{count} mimo provoz",
    "monitoring.pods.tile.inFlight": "Probíhající",
    "monitoring.pods.tile.inFlightSub": "z {max} souběžných slotů",
    "monitoring.pods.tile.queued": "Ve frontě",
    "monitoring.pods.tile.queuedSub": "čekají na volný slot",
    "monitoring.pods.tile.timeout": "Časový limit požadavku",
    "monitoring.pods.tile.timeoutSub": "max. počet pokusů {attempts}, každý na jiném podu",
    "monitoring.pods.tile.attempts": "Limity pokusů",
    "monitoring.pods.tile.attemptsCount.one": "{count} pokus",
    "monitoring.pods.tile.attemptsCount.few": "{count} pokusy",
    "monitoring.pods.tile.attemptsCount.other": "{count} pokusů",
    "monitoring.pods.tile.attemptsSub": "nejvýš {total} s na volání",
    "monitoring.pods.tile.attemptsHint":
      "Požadavek, který neodpoví včas, se pošle znovu (nejdřív na jiný pod, s jediným podem na ten samý), pokaždé s delším limitem; po posledním pokusu volání selže.",
    "monitoring.pods.tile.keeper": "Udržování spojení",
    "monitoring.pods.tile.keeperSub": "drží TCP okno nečinných podů zahřáté",
    "monitoring.pods.tile.keeperHint":
      "Když je pod nečinný, backend každých 0,2 s pošle na /drain malý fiktivní upload, aby se TCP okno zahřátého spojení nezhroutilo.",
    "monitoring.pods.tile.keeper404.one": "{count} pod bez /drain",
    "monitoring.pods.tile.keeper404.few": "{count} pody bez /drain",
    "monitoring.pods.tile.keeper404.other": "{count} podů bez /drain",
    "monitoring.pods.tile.session": "HTTP relace",
    "monitoring.pods.tile.sessionOpen": "otevřená",
    "monitoring.pods.tile.sessionClosed": "zatím neotevřená",
    "monitoring.pods.tile.sessionSub": "otevře ji první požadavek na inferenci",
    "monitoring.pods.tile.source": "Seznam podů",
    "monitoring.pods.source.autoscaler": "Autoscaler",
    "monitoring.pods.source.autoscalerSub": "snapshot pod_servers",
    "monitoring.pods.source.static": "Statický",
    "monitoring.pods.source.staticSub": "nastavení pod_pool_static_servers",
    "monitoring.pods.source.custom": "Vlastní",
    "monitoring.pods.source.customSub": "nastaveno v kódu",
    "monitoring.pods.tile.lastUpdate": "Aktualizace z autoscaleru",
    "monitoring.pods.tile.lastUpdateNever": "zatím žádná",
    "monitoring.pods.tile.lastUpdateSub": "+{added} / −{removed} · v poolu {total}",
    "monitoring.pods.tile.lastUpdateStatic": "statický pool se neaktualizuje",
    "monitoring.pods.tile.lastUpdateWaiting": "od startu backendu nepřišel žádný snapshot",
    "monitoring.pods.update.title": "Poslední změna sady podů",
    "monitoring.pods.update.added": "Přidány",
    "monitoring.pods.update.removed": "Odebrány",
    "monitoring.pods.config.title": "Nastaveno vs. běží",
    "monitoring.pods.config.summary": "{configured} v pod_servers · {running} v poolu",
    "monitoring.pods.config.mismatch":
      "Pool se liší od snapshotu autoscaleru v pod_servers (aktualizace nedorazila nebo selhala)",
    "monitoring.pods.config.missing": "V pod_servers, neběží:",
    "monitoring.pods.config.extra": "Běží, není v pod_servers:",
    "monitoring.pods.config.match": "Pool odpovídá pod_servers.",
    "monitoring.pods.config.noPool": "Porovná se, jakmile pool vznikne.",
    "monitoring.pods.config.static": "Statický pool: pod_servers (počet záznamů: {configured}) se nepoužívá.",
    "monitoring.pods.config.notUsed": "Pool nepochází z pod_servers (počet záznamů: {configured}); není co porovnat.",
    "monitoring.pods.config.unavailable": "pod_servers se nepodařilo načíst.",

    "monitoring.pods.cards.title": "Pody",
    "monitoring.pods.cards.empty":
      "V poolu není žádný pod. Se zdrojem autoscaler selže každé vyhodnocení, dokud nepošle snapshot s podem.",
    "monitoring.pods.cards.noPool": "Zatím žádný pod: pool vznikne při prvním vyhodnocení.",
    "monitoring.pods.health.healthy": "V pořádku",
    "monitoring.pods.health.unhealthy": "Mimo provoz",
    "monitoring.pods.health.failing": "Selhává",
    "monitoring.pods.health.changed": "změna stavu",
    "monitoring.pods.health.unchanged": "od startu beze změny stavu",
    "monitoring.pods.locality.none": "bez lokality",
    "monitoring.pods.ping.notReady": "nepřipraven",
    "monitoring.pods.ping.never": "zatím bez pingu",
    "monitoring.pods.ping.notReadyDetail": "/ping neodpovídá 200: pod se zahřívá nebo nefunguje",
    "monitoring.pods.stat.ping": "Ping",
    "monitoring.pods.stat.pingHint":
      "Nejrychlejší ze 3 souběžných volání /ping každé 2 s; vytížený pod v pořádku se nepinguje, jeho provoz dokazuje, že žije.",
    "monitoring.pods.stat.ewma": "Latence (EWMA)",
    "monitoring.pods.stat.ewmaSub": "úspěšné požadavky",
    "monitoring.pods.stat.ewmaHint":
      "Exponenciálně vyhlazená latence úspěšných požadavků (α 0,3); jen pro statistiku, směrování ji nepoužívá.",
    "monitoring.pods.stat.inFlight": "Probíhající",
    "monitoring.pods.stat.inFlightSub": "požadavky na podu právě teď",
    "monitoring.pods.stat.requests": "Požadavky",
    "monitoring.pods.stat.requestsSub": "{ok} ok · {fail} chyb · {timeout} vypršelo",
    "monitoring.pods.stat.failureRate": "Podíl chyb",
    "monitoring.pods.stat.sinceStart": "od startu backendu",
    "monitoring.pods.stat.consecutive": "Chyby za sebou",
    "monitoring.pods.stat.consecutiveSub": "při {limit} se vyřadí",
    "monitoring.pods.stat.lastSuccess": "Poslední úspěch",
    "monitoring.pods.stat.lastFailure": "Poslední chyba",
    "monitoring.pods.stat.deactivations": "Vyřazení",
    "monitoring.pods.stat.deactivationsHint":
      "Kolikrát byl pod od startu backendu vyřazen z rotace (3 chyby za sebou nebo neúspěšný ping)",
    "monitoring.pods.stat.idle": "Nečinný",
    "monitoring.pods.stat.idleNever": "zatím nepoužit",
    "monitoring.pods.stat.idleHint": "Čas od posledního dokončeného skutečného požadavku (dávky keeperu se nepočítají)",
    "monitoring.pods.keeper.label": "Keeper",
    "monitoring.pods.keeper.running": "běží",
    "monitoring.pods.keeper.stopped": "neběží",
    "monitoring.pods.keeper.disabled": "vypnutý",
    "monitoring.pods.keeper.missingDrain": "chybí /drain",
    "monitoring.pods.keeper.404Detail":
      "Pod nemá endpoint /drain (starý build serving_backend): každá dávka keeperu zavře spojení z poolu. Nasaďte pod znovu nebo keeper vypněte.",
    "monitoring.pods.lastError": "Poslední chyba",
    "monitoring.pods.phases.title": "Poslední požadavek",
    "monitoring.pods.phases.none": "Od startu backendu žádný úspěšný požadavek.",
    "monitoring.pods.phases.meta": "celkem {total} · {in} KB tam · {out} KB zpět",
    "monitoring.pods.phase.lock": "zámek",
    "monitoring.pods.phase.conn": "spojení",
    "monitoring.pods.phase.upload": "upload",
    "monitoring.pods.phase.pod": "pod",
    "monitoring.pods.phase.read": "čtení",
    "monitoring.pods.phase.reused": "znovu použité",
    "monitoring.pods.phase.new": "NOVÉ",
    "monitoring.pods.phase.lockHint": "Čekání, než dávka keeperu uvolní zahřáté spojení",
    "monitoring.pods.phase.connHint":
      "Získání spojení: znovu použité = zahřátý socket z poolu, NOVÉ = handshake (zahřátý socket se ztratil)",
    "monitoring.pods.phase.uploadHint": "Předání těla jádru; roste, jen když se zaplní odesílací buffer",
    "monitoring.pods.phase.podHint":
      "Odeslané tělo → hlavičky odpovědi: skutečný přenos po síti (zde se projeví TCP slow start), dekódování, GPU inference a jeden round trip",
    "monitoring.pods.phase.readHint": "Stažení těla odpovědi",
    "monitoring.pods.phase.newWarning":
      "Nové spojení: zahřátý socket se ztratil, požadavek zaplatil handshake a TCP slow start",
    "monitoring.pods.recent.title": "Poslední požadavky",
    "monitoring.pods.recent.summary": "{n} od {time} · p50 {p50} · p95 {p95} · max {max}",
    "monitoring.pods.recent.failures.one": "{count} selhal",
    "monitoring.pods.recent.failures.few": "{count} selhaly",
    "monitoring.pods.recent.failures.other": "{count} selhalo",
    "monitoring.pods.recent.empty": "Od startu backendu na tento pod nepřišel žádný požadavek.",
    "monitoring.pods.recent.latency": "Latence",
    "monitoring.pods.recent.failed": "Chyba",

    "monitoring.pods.side.title": "Na podu",
    "monitoring.pods.side.polled": "načteno",
    "monitoring.pods.side.pollHint": "Backend čte GET /metrics podu každých 15 s",
    "monitoring.pods.side.unsupported": "Image podu bez /metrics (starší než v0.0.39): žádná data o GPU ani z podu.",
    "monitoring.pods.side.waiting": "Čeká se na první načtení /metrics (každých 15 s).",
    "monitoring.pods.side.pollFailed": "GET /metrics selhalo: {error}",
    "monitoring.pods.side.lastGood": "poslední úspěšné načtení",
    "monitoring.pods.side.image": "Image",
    "monitoring.pods.side.pcieOf": "(z {max})",
    "monitoring.pods.side.pcieHint":
      "Nejlepší linka PCIe od startu podu (při nečinnosti GPU se zpomalí); užší linka, než karta umí (x8 místo x16), už dřív zpomalovala pody",
    "monitoring.pods.side.uptime": "běží {duration}",
    "monitoring.pods.side.readyAfter": "připraven po {duration}",
    "monitoring.pods.side.preloadFailed": "Modely se nepodařilo načíst, pod nebude nikdy připraven",
    "monitoring.pods.side.onCpuWarning": "Běží na CPU místo na GPU (10–50× pomaleji): {models}",
    "monitoring.pods.side.loopBlocked": "Event loop podu byl za 5 min zablokovaný až {max}",
    "monitoring.pods.side.noGpu": "Žádná data o GPU z podu: {error}",
    "monitoring.pods.side.util": "Vytížení GPU",
    "monitoring.pods.side.utilSub": "průměr 5 min {avg} · max {max}",
    "monitoring.pods.side.memory": "Paměť GPU",
    "monitoring.pods.side.memorySub": "z {total} · max 5 min {max}",
    "monitoring.pods.side.temperature": "Teplota",
    "monitoring.pods.side.temperatureSub": "max 5 min {max}",
    "monitoring.pods.side.power": "Příkon",
    "monitoring.pods.side.powerSub": "limit {limit} · průměr 5 min {avg}",
    "monitoring.pods.side.sparkHint": "Čára: posledních 15 min (vzorek každých 5 s), až po limit karty",
    "monitoring.pods.side.throttle": "Takty omezeny",
    "monitoring.pods.side.throttleCapHint": "Karta jede na limitu příkonu: při zátěži normální",
    "monitoring.pods.side.throttleSlowHint": "Takty snížené kvůli teplotě nebo hardwaru: inference zpomalí",
    "monitoring.pods.side.smClock": "SM",
    "monitoring.pods.side.models": "Modely",
    "monitoring.pods.side.model.crust": "Kůrka",
    "monitoring.pods.side.model.components": "Suroviny",
    "monitoring.pods.side.model.sauces": "Omáčky",
    "monitoring.pods.side.model.obstacles": "Překážky",
    "monitoring.pods.side.model.slicing": "Krájení",
    "monitoring.pods.side.onGpu": "GPU",
    "monitoring.pods.side.onCpu": "CPU",
    "monitoring.pods.side.notLoaded": "nenačten",
    "monitoring.pods.side.cpuHint": "Běží na CPU (ONNX Runtime přešel na CPU): 10–50× pomaleji",
    "monitoring.pods.side.nvjpegHint": "Fotky se dekódují na GPU (nvJPEG)",
    "monitoring.pods.side.cpuDecode": "dekódování na CPU",
    "monitoring.pods.side.cpuDecodeHint": "Fotky se dekódují na CPU (nvJPEG není aktivní)",
    "monitoring.pods.side.routes": "Latence na podu · 5 min",
    "monitoring.pods.side.routesHint": "Čas uvnitř podu od přijetí požadavku po odpověď, bez sítě",
    "monitoring.pods.side.col.count": "Požadavky",
    "monitoring.pods.side.col.errors": "Chyby",
    "monitoring.pods.side.clientErrorsHint": "Odmítnuté požadavky (4xx), např. výřez mimo fotku",
    "monitoring.pods.side.noInference": "Od startu podu žádný požadavek na inferenci.",
    "monitoring.pods.side.otherRoutes": "Ostatní požadavky za 5 min:",
    "monitoring.pods.side.other": "neznámé cesty",
    "monitoring.pods.side.otherHint": "Požadavky na neznámé cesty: internetové skenery zkoušejí veřejný port podu",
    "monitoring.pods.side.errors": "Poslední chyby na podu",
    "monitoring.pods.side.errorsAll": "Zobrazit vše ({count})",
    "monitoring.pods.side.errorsFewer": "Zobrazit méně",
    "monitoring.pods.side.noMessage": "bez zprávy",
    "monitoring.pods.side.details": "Podrobnosti podu",
    "monitoring.pods.side.hideDetails": "Skrýt podrobnosti podu",
    "monitoring.pods.side.stages": "Kam jde čas podu · p50 5 min",
    "monitoring.pods.side.stagesTotal": "celý požadavek p50 {p50}",
    "monitoring.pods.side.stagesNote":
      "Pruhy: p50 kroku jako podíl z p50 celého požadavku. /analyze/raw počítá suroviny, překážky a krájení souběžně, proto je součet jeho kroků vyšší.",
    "monitoring.pods.side.stagesEmpty": "Za posledních 5 min žádná inference.",
    "monitoring.pods.side.startup": "Start (poslední běh)",
    "monitoring.pods.side.process": "Event loop, proces a hostitel",
    "monitoring.pods.side.loop": "Zpoždění event loopu (5 min)",
    "monitoring.pods.side.loopValue": "max {max} · p99 {p99}",
    "monitoring.pods.side.loopHint":
      "O kolik později se na podu probudí 0,5s časovač; samotná inference kůrky blokuje loop ~150–250 ms, do ~300 ms je to normální",
    "monitoring.pods.side.over100": "Vzorky nad 100 ms (5 min)",
    "monitoring.pods.side.over100Hint": "Vzorky loopu (každých 0,5 s), které se probudily o víc než 100 ms později",
    "monitoring.pods.side.processCpu": "CPU procesu",
    "monitoring.pods.side.rss": "Paměť procesu (RSS)",
    "monitoring.pods.side.nowMax": "{now} · max 5 min {max}",
    "monitoring.pods.side.hostCpu": "CPU hostitele",
    "monitoring.pods.side.hostCpuValue": "{pct} · jader {cores} · zátěž {load}",
    "monitoring.pods.side.hostMemory": "Paměť hostitele",
    "monitoring.pods.side.driver": "Ovladač",
    "monitoring.pods.side.pcieNow": "Linka PCIe teď",
    "monitoring.pods.side.pcieNowHint": "Při nečinnosti GPU se linka zpomalí (Gen1 v klidu je normální)",
    "monitoring.pods.side.uptimeLabel": "Doba běhu",
    "monitoring.pods.side.podId": "ID podu v RunPodu",

    "monitoring.pods.window.title": "Historie · {window}",
    "monitoring.pods.window.gpu": "GPU na podech · {window}",
    "monitoring.pods.chart.empty": "V tomto období žádná inference",
    "monitoring.pods.chart.requests": "Pokusy o inferenci",
    "monitoring.pods.chart.requestsDescription":
      "Pokusy v intervalu podle výsledku; neúspěšný pokus se zopakuje na jiném podu",
    "monitoring.pods.series.ok": "OK",
    "monitoring.pods.series.fail": "Chyba",
    "monitoring.pods.series.timeout": "Vypršel čas",
    "monitoring.pods.chart.latency": "Latence inference",
    "monitoring.pods.chart.latencyDescription":
      "Celý požadavek včetně čekání ve frontě a opakování; p95 kůrky a surovin v legendě",
    "monitoring.pods.series.crustP95": "Kůrka p95",
    "monitoring.pods.series.componentsP95": "Suroviny p95",
    "monitoring.pods.chart.slow": "pomalé",
    "monitoring.pods.chart.perPod": "Latence p95 podle podu",
    "monitoring.pods.chart.perPodDescription": "Jeden úspěšný pokus na podu.",
    "monitoring.pods.chart.extraPods": "Pody za čtvrtým jsou šedé a skryté; zobrazíte je v legendě.",
    "monitoring.pods.chart.ping": "Ping p95 podle podu",
    "monitoring.pods.chart.pingDescription": "Doba odezvy /ping, měří se, když je pod nečinný.",
    "monitoring.pods.chart.noPing": "V tomto období žádný ping",
    "monitoring.pods.chart.queue": "Čekání ve frontě",
    "monitoring.pods.chart.queueDescription": "Jak dlouho požadavek čekal na jeden z {max} souběžných slotů",
    "monitoring.pods.chart.noQueue": "V tomto období žádný požadavek nečekal",
    "monitoring.pods.series.max": "max",
    "monitoring.pods.chart.concurrency": "Probíhající a ve frontě",
    "monitoring.pods.chart.concurrencyDescription": "Maxima v intervalu; fronta vzniká, jen když jsou obsazené všechny sloty",
    "monitoring.pods.series.inFlight": "Probíhající (max)",
    "monitoring.pods.series.queued": "Ve frontě (max)",
    "monitoring.pods.chart.slots": "limit {max}",
    "monitoring.pods.chart.noConcurrency": "V žádném vzorku nic neprobíhalo ani nečekalo (měří se každých 10 s)",
    "monitoring.pods.chart.health": "Pody v pořádku",
    "monitoring.pods.chart.healthDescription":
      "Nejméně podů v pořádku a největší pool v intervalu; červené sloupce značí pody mimo rotaci",
    "monitoring.pods.series.healthy": "V pořádku (min)",
    "monitoring.pods.series.total": "V poolu (max)",
    "monitoring.pods.series.unhealthy": "Mimo provoz",
    "monitoring.pods.chart.noHealth": "V tomto období žádný vzorek poolu",
    "monitoring.pods.chart.retries": "Opakování a chyby poolu",
    "monitoring.pods.chart.retriesDescription":
      "Požadavky úspěšné až po opakování a požadavky, které selhaly na všech podech (vyhodnocení selže)",
    "monitoring.pods.series.retriedOk": "OK po opakování",
    "monitoring.pods.series.poolError": "Selhal na všech podech",
    "monitoring.pods.chart.noRetries": "Žádné opakování ani chyba poolu ({window})",
    "monitoring.pods.chart.gpuUtil": "Vytížení GPU podle podu",
    "monitoring.pods.chart.gpuUtilDescription": "Nejvyšší minutový průměr v intervalu, z vlastních /metrics podů.",
    "monitoring.pods.chart.gpuMemory": "Paměť GPU podle podu",
    "monitoring.pods.chart.gpuMemoryDescription": "Maximum využité paměti GPU v intervalu.",
    "monitoring.pods.chart.gpuTemperature": "Teplota GPU podle podu",
    "monitoring.pods.chart.gpuTemperatureDescription": "Maximum v intervalu; od {hot} °C je GPU přehřáté.",
    "monitoring.pods.chart.hot": "přehřáté",
    "monitoring.pods.chart.gpuPower": "Příkon GPU podle podu",
    "monitoring.pods.chart.gpuPowerDescription": "Nejvyšší minutový průměr v intervalu.",
    "monitoring.pods.chart.podLoop": "Zpoždění event loopu na podech",
    "monitoring.pods.chart.podLoopDescription":
      "Maximum v intervalu; samotná inference kůrky blokuje loop podu ~150–250 ms.",
    "monitoring.pods.chart.noGpu":
      "V tomto období žádný pod nehlásil metriky GPU (GET /metrics má image podu od v0.0.39).",

    "monitoring.pods.totals.title": "Podle podu za období",
    "monitoring.pods.totals.description": "Pokusy, chyby a latence jednotlivých pokusů ({window})",
    "monitoring.pods.col.pod": "Pod",
    "monitoring.pods.col.attempts": "Pokusy",
    "monitoring.pods.col.failures": "Chyby",
    "monitoring.pods.col.failureRate": "Podíl chyb",
    "monitoring.pods.col.p50": "p50",
    "monitoring.pods.col.p95": "p95",
    "monitoring.pods.col.max": "max",
    "monitoring.pods.col.ping": "Ping p95",
    "monitoring.pods.totals.timeouts": "(vypršelo {count})",
    "monitoring.pods.totals.all": "Všechny pody",
    "monitoring.pods.totals.note":
      "Podle podu: latence jednoho úspěšného pokusu. Všechny pody: celý požadavek včetně čekání ve frontě a opakování.",
    "monitoring.pods.totals.notInPool": "už není v poolu",
    "monitoring.pods.totals.empty": "V tomto období žádný pokus o inferenci.",

    "monitoring.backend.on": "zapnuto",
    "monitoring.backend.off": "vypnuto",
    "monitoring.backend.process.title": "Proces",
    "monitoring.backend.process.description": "Proces backendu, se kterým admin komunikuje",
    "monitoring.backend.process.instance": "Instance",
    "monitoring.backend.process.role": "Prostředí",
    "monitoring.backend.process.version": "Verze",
    "monitoring.backend.process.pid": "PID",
    "monitoring.backend.process.hostname": "Hostitel",
    "monitoring.backend.process.python": "Python",
    "monitoring.backend.process.platform": "Platforma",
    "monitoring.backend.process.started": "Spuštěno",
    "monitoring.backend.process.uptime": "Doba běhu",
    "monitoring.backend.process.recentRestart":
      "Restart před méně než 10 min: živá data (čítače podů, poslední hodina) začínají od nuly.",
    "monitoring.backend.loop.title": "Zpoždění event loopu",
    "monitoring.backend.loop.description":
      "O kolik později se probudí 250ms časovač: zablokovaný loop (synchronní výpočet, blokující volání) se projeví nejdřív tady",
    "monitoring.backend.loop.current": "Teď",
    "monitoring.backend.loop.max1m": "Max za 1 min",
    "monitoring.backend.loop.max5m": "Max za 5 min",
    "monitoring.backend.loop.thresholds": "Varování od 300 ms, zablokovaný od 1 s.",
    "monitoring.backend.loop.live": "Poslední hodina · max za 5 s",
    "monitoring.backend.loop.window": "Max v intervalu · {window}",
    "monitoring.backend.loop.lag": "Zpoždění",
    "monitoring.backend.loop.warnLine": "varování",
    "monitoring.backend.loop.nokLine": "zablokováno",
    "monitoring.backend.loop.empty": "Zatím žádný vzorek zpoždění",

    "monitoring.backend.res.title": "Prostředky",
    "monitoring.backend.res.description":
      "Aktuální vzorek (každých 10 s), poslední hodina této instance a maxima za období",
    "monitoring.backend.res.sampled": "změřeno",
    "monitoring.backend.res.cpu": "CPU",
    "monitoring.backend.res.cpuSub": "100 % = jedno jádro",
    "monitoring.backend.res.rss": "Paměť (RSS)",
    "monitoring.backend.res.tasks": "Asyncio tasky",
    "monitoring.backend.res.threads": "Vlákna",
    "monitoring.backend.res.fds": "Otevřené soubory",
    "monitoring.backend.res.writes": "Zápisy do S3 na pozadí",
    "monitoring.backend.res.writesSub": "vrstvy fotek čekající na S3",
    "monitoring.backend.res.inference": "Probíhající inference",
    "monitoring.backend.res.http": "Probíhající HTTP",
    "monitoring.backend.res.monitored": "Sledované tasky",
    "monitoring.backend.res.monitoredSub": "úlohy na pozadí s hlášením chyb",
    "monitoring.backend.res.live": "Poslední hodina · vzorky každých 10 s",
    "monitoring.backend.res.window": "Maxima v intervalu · {window}",
    "monitoring.backend.res.chart.cpu": "CPU",
    "monitoring.backend.res.chart.memory": "Paměť (RSS)",
    "monitoring.backend.res.chart.counts": "Tasky, vlákna a otevřené soubory",
    "monitoring.backend.res.chart.work": "Rozpracovaná práce",
    "monitoring.backend.res.chart.cpuMax": "CPU (max)",
    "monitoring.backend.res.chart.memoryMax": "Paměť (max)",
    "monitoring.backend.res.chart.tasksMax": "Asyncio tasky (max)",
    "monitoring.backend.res.chart.workMax": "Rozpracovaná práce (max)",
    "monitoring.backend.res.backlog": "zahlcení",
    "monitoring.backend.res.noSamples": "Zatím žádný vzorek (měří se každých 10 s)",
    "monitoring.backend.res.noWork": "V žádném vzorku nic neprobíhalo",

    "monitoring.backend.deps.title": "Závislosti",
    "monitoring.backend.deps.description": "Dostupnost podle kontroly backendu (cache 30 s)",
    "monitoring.backend.deps.checked": "zkontrolováno",
    "monitoring.backend.deps.mongo": "MongoDB",
    "monitoring.backend.deps.s3": "Úložiště S3",
    "monitoring.backend.deps.thingsboard": "ThingsBoard",
    "monitoring.backend.deps.ok": "Dostupné",
    "monitoring.backend.deps.error": "Nedostupné",
    "monitoring.backend.deps.slow": "pomalé",
    "monitoring.backend.deps.pods": "GPU pody",
    "monitoring.backend.deps.podsEmpty": "V poolu není žádný pod.",
    "monitoring.backend.deps.pingHint": "Poslední ping podu",
    "monitoring.backend.deps.empty": "Backend nevrátil žádnou kontrolu.",
    "monitoring.backend.models.title": "Modely",
    "monitoring.backend.models.description": "ONNX modely tohoto procesu; každý se načte při prvním použití",
    "monitoring.backend.models.loaded": "Načten",
    "monitoring.backend.models.notLoaded": "Nenačten",
    "monitoring.backend.models.version": "Verze",
    "monitoring.backend.models.threads": "Vlákna",
    "monitoring.backend.models.threshold": "Práh",
    "monitoring.backend.models.empty": "Žádné informace o modelech.",
    "monitoring.backend.models.name.burnt_cnn": "Spálená kůrka",
    "monitoring.backend.models.name.blur_cnn": "Kontrola rozmazání",
    "monitoring.backend.models.name.obstacle_seg": "Překážky",
    "monitoring.backend.models.name.slicing_cnn": "Krájení",
    "monitoring.backend.settings.title": "Nastavení",
    "monitoring.backend.settings.description": "Snímek nastavení, která mění chování (bez tajných údajů)",
    "monitoring.backend.settings.general": "Obecné",
    "monitoring.backend.settings.empty": "Backend nevrátil žádné nastavení.",

    "monitoring.backend.mon.title": "Stav monitoringu",
    "monitoring.backend.mon.description": "Zda se ukládají data samotného monitoringu",
    "monitoring.backend.mon.persist": "Ukládání",
    "monitoring.backend.mon.persistOn": "MongoDB",
    "monitoring.backend.mon.persistOff": "jen v paměti",
    "monitoring.backend.mon.persistFailed": "jen v paměti (nastavení selhalo)",
    "monitoring.backend.mon.metricsFlush": "Metriky uloženy",
    "monitoring.backend.mon.eventsFlush": "Události uloženy",
    "monitoring.backend.mon.never": "nikdy",
    "monitoring.backend.mon.metricsError": "Ukládání metrik selhává",
    "monitoring.backend.mon.eventsError": "Ukládání událostí selhává",
    "monitoring.backend.mon.dropped": "Zahozené události",
    "monitoring.backend.mon.droppedHint": "Události ztracené kvůli přetečení bufferu v paměti",
    "monitoring.backend.mon.monitoredTasks": "Běžící sledované tasky",
    "monitoring.backend.mon.recorded": "Události od startu",
    "monitoring.backend.int.title": "Integrace",
    "monitoring.backend.int.description": "Poslední volání, která tato instance od startu přijala",
    "monitoring.backend.int.biteberry.order": "Objednávky BiteBerry",
    "monitoring.backend.int.biteberry.item": "Položky BiteBerry",
    "monitoring.backend.int.biteberry.status": "Stav BiteBerry",
    "monitoring.backend.int.autoscaler": "Snapshoty autoscaleru",
    "monitoring.backend.int.calls.one": "{count} volání",
    "monitoring.backend.int.calls.few": "{count} volání",
    "monitoring.backend.int.calls.other": "{count} volání",
    "monitoring.backend.int.never": "od startu žádné",
    "monitoring.backend.inst.title": "Instance",
    "monitoring.backend.inst.description":
      "Procesy backendu, které za posledních 24 h zapsaly metriky; monitoring lze omezit na jeden z nich",
    "monitoring.backend.inst.col.instance": "Instance",
    "monitoring.backend.inst.col.role": "Prostředí",
    "monitoring.backend.inst.col.first": "Poprvé",
    "monitoring.backend.inst.col.last": "Naposledy",
    "monitoring.backend.inst.col.requests": "Požadavky (24 h)",
    "monitoring.backend.inst.col.action": "Rozsah",
    "monitoring.backend.inst.scope": "Jen tato",
    "monitoring.backend.inst.scoped": "Zobrazeno",
    "monitoring.backend.inst.clear": "Zpět na prostředí",
    "monitoring.backend.inst.empty": "Za posledních 24 h nezapsala metriky žádná instance (ukládání vypnuto?).",
  },
});
